/* Copyright (c) 2020-present CloudIO Inc */

import { ItemType } from '../../registry/ComponentRegistry';
import { toOptions } from 'componentregistry/utils/ManagerUtils';
import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { getManager } from '../../registry/Manager';
import { defaultValue } from './Comments';

const CommentsManager: ComponentManagerFn<'Comments'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'Comments'>(platform, 'Comments'),
    isChildAllowed: (page, item, childType) =>
      (
        [
          'CommentActionMenuItems',
          'TriggerOnComment',
          'TriggerOnMention',
        ] as ItemType[]
      ).includes(childType),
    type: 'Comments',
    name: 'Comments',
    defaultValue,
    props: {
      header: {
        label: 'Header',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'The title to be displayed on the comments drawer.',
      },
      placeholder: {
        label: 'Placeholder',
        type: 'expression',
        returnType: async () => 'string',
        helpText:
          'A placeholder that will be displayed in the comment box before the user starts typing the comments.',
      },
      context: {
        label: 'Context',
        type: 'string',
        placeholder: 'e.g. InvoiceComments',
        helpText:
          'A unique context across your application. e.g. OrderComments, InvoiceComments etc.',
      },
      contextValue: {
        label: 'Context Value',
        type: 'expression',
        helpText: `A value within the above context to seperate out the comments.

You can provide a static text, e.g. "All", if you don't want to seperate the comments.

To have different comments per record or record type, you can provide an expression e.g. \`\${$.alias.invoiceId}\` or \`\${$.alias.invoiceType}\``,
        returnType: async () => 'string',
      },
      position: {
        label: 'Position',
        type: 'select',
        options: toOptions(['left', 'right']),
        helpText:
          'The position on the screen from which side the comments drawer should be drawn out.',
      },
      open: {
        label: 'Open',
        type: 'boolean',
        helpText:
          'Leave this off to show the comments drawer only when the user clicks the comments Icon. If this is on, then the comments drawer will be displayed as soon as the user navigates to this page.',
      },
    },
    getDisplayText: (item, expanded, { appUid, pageId }) => {
      const page = platform.getPage(appUid, pageId);
      const itemIds = Object.keys(page);
      const usage = itemIds.filter((id) => {
        const itm = page[id];
        const mgr = getManager(itm.type);
        return mgr.isItemIdUsed(itm, item.itemId);
      });
      return usage.length
        ? `${item.props.context} (${usage.length} usage${
            usage.length > 1 ? 's' : ''
          })`
        : item.props.context;
    },
    category: 'Data',
    canHaveChildren: () => false,
    noOverlay: true,
    icon: { iconName: 'comments' },
    description: `Comments component is used to write comments or reviews on any record by setting context and context value. Comments will be displayed in a Drawer. A Drawer is a side sheet containing supplementary content that are anchored to the left or right edge of the screen.

For instance, if you want the users to be able to comment on invoices. You can set the context to \`InvoiceComments\` and the context value to \`$.InvoiceStoreAlias.invoiceId\`

\`\`\`alert_warning
Note: The expression for context value must return a string value. If the invoiceId attribute in the above example is a number, enclose it within a backtick  to convert it to a string e.g. \`\${$.InvoiceStoreAlias.invoiceId}\`
\`\`\`

To open the comments drawer, use an Icon or Icon Column anywhere on the page and set the 'Comments Item ID' prop to the Item ID of this Comments component.
`,
  };
};

export default CommentsManager;
